import { Component, OnInit } from '@angular/core';
import { NotificationService } from '../../../_core/_services/notification.service';
import { ComplianceAdminService } from '../../../_core/_services/_compliance/compliance-admin.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin } from 'rxjs';
import { ModalCleanupService } from '../../../_core/_services/modal-cleanup.service';

@Component({
  selector: 'app-admin-policy-rules',
  standalone: false,
  templateUrl: './admin-policy-rules.html',
  styleUrl: './admin-policy-rules.css',
})
export class AdminPolicyRules implements OnInit {
  loading = false;
  saving = false;
  importing = false;
  deletingId: string | null = null;
  error: string | null = null;

  policyId = '';
  policy: any = null;
  policies: any[] = [];
  items: any[] = [];

  form!: FormGroup;
  importForm!: FormGroup;
  editingId: string | null = null;
  rulePendingDelete: any = null;

  availableFields: any[] = [];
  availableDocuments: any[] = [];

  ruleTypeOptions = [
    'RequiredFields',
    'RequiredDocuments',
    'MinimumAge',
    'DocumentNotExpired',
    'SelfieMatch',
    'SanctionsScreening',
    'Requirement',
    'Validation',
    'Risk',
    'Screening',
    'Workflow'
  ];

  ruleCodeOptions = [
  'BASIC_REQUIRED_FIELDS',
  'BASIC_REQUIRED_DOCUMENTS',
  'MINIMUM_AGE_18',
  'ID_NOT_EXPIRED',
  'SELFIE_MATCH_REQUIRED',
  'SANCTIONS_SCREENING',

  // Generic/Future Rules
  'REQUIRED_FIELDS',
  'REQUIRED_DOCUMENTS',
  'MINIMUM_AGE',
  'DOCUMENT_NOT_EXPIRED',
  'SELFIE_MATCH',
  'SANCTIONS_CHECK',
  'PEP_SCREENING',
  'SOURCE_OF_FUNDS_REQUIRED',
  'PROOF_OF_ADDRESS_REQUIRED',
  'MANUAL_REVIEW_REQUIRED',
  'HIGH_RISK_COUNTRY_CHECK',
  'MAX_DAILY_DEPOSIT',
  'MAX_DAILY_WITHDRAWAL',
  'MAX_TRANSACTION_AMOUNT',
  'MAX_MONTHLY_VOLUME'
];

  ruleTypes = this.ruleTypeOptions;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private adminService: ComplianceAdminService,
    private notifyService: NotificationService,
    private modalCleanup: ModalCleanupService,
  ) { }

  ngOnInit(): void {
    this.policyId = this.route.snapshot.paramMap.get('id') || '';

    this.form = this.fb.group({
      ruleCode: ['', [Validators.required, Validators.maxLength(100)]],
      ruleType: ['RequiredFields', [Validators.required]],
      isBlocking: [true],
      orderIndex: [0, [Validators.required, Validators.min(0)]],
      configJson: [''],

      requiredFieldKeys: [[]],
      requiredDocumentTypeCodes: [[]],
      minimumAgeFieldKey: ['date_of_birth'],
      minimumAge: [18],
      documentNotExpiredTypeCodes: [[]],
      selfieIdDocumentTypeCode: [''],
      selfieDocumentTypeCode: ['SELFIE'],
      sanctionsIdDocumentTypeCode: ['']
    });

    this.importForm = this.fb.group({
      sourcePolicyId: ['', [Validators.required]],
      overwriteExisting: [false]
    });

    if (!this.policyId) {
      this.error = 'Policy not found.';
      return;
    }

    this.loadPolicy();
    this.loadPolicies();
    this.loadItems();

    this.loadPolicyFields();
    this.loadPolicyDocuments();
  }

  loadPolicyFields(): void {
    this.adminService.getPolicyFields(this.policyId).subscribe({
      next: (res: any) => {
        this.availableFields = res?.data || res || [];
      },
      error: () => {
        this.availableFields = [];
      }
    });
  }

  loadPolicyDocuments(): void {
    this.adminService.getPolicyDocuments(this.policyId).subscribe({
      next: (res: any) => {
        this.availableDocuments = res?.data || res || [];
      },
      error: () => {
        this.availableDocuments = [];
      }
    });
  }

  loadPolicy(): void {
    this.adminService.getPolicyById(this.policyId).subscribe({
      next: (res: any) => {
        this.policy = res?.data || res || null;
      },
      error: () => {
        this.policy = null;
      }
    });
  }

  loadPolicies(): void {
    this.adminService.getPolicies({ page: 1, pageSize: 1000 }).subscribe({
      next: (res: any) => {
        this.policies = res?.data || res || [];
      },
      error: () => {
        this.policies = [];
      }
    });
  }

  loadItems(): void {
    this.loading = true;
    this.error = null;

    this.adminService.getPolicyRules(this.policyId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res: any) => {
          this.items = res?.data || res || [];
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to load policy rules.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  importRules(): void {
    this.importForm.markAllAsTouched();

    if (this.importForm.invalid) return;

    const sourcePolicyId = String(this.importForm.value.sourcePolicyId || '').trim();

    if (!sourcePolicyId || sourcePolicyId === this.policyId) return;

    this.importing = true;
    this.error = null;

    this.adminService.getPolicyRules(sourcePolicyId)
      .subscribe({
        next: (res: any) => {
          const sourceRules = res?.data || res || [];

          if (!sourceRules.length) {
            this.importing = false;
            this.notifyService.showInfo('The selected policy has no rules to import.', 'No Rules');
            return;
          }

          const overwriteExisting = !!this.importForm.value.overwriteExisting;
          const requests = sourceRules
            .map((rule: any) => this.buildImportRequest(rule, overwriteExisting))
            .filter(Boolean);

          if (!requests.length) {
            this.importing = false;
            this.notifyService.showInfo('All selected policy rules already exist on this policy.', 'Nothing Imported');
            return;
          }

          forkJoin(requests)
            .pipe(finalize(() => (this.importing = false)))
            .subscribe({
              next: () => {
                this.notifyService.showSuccess('Policy rules imported successfully.', 'Success');
                this.loadItems();
              },
              error: (err) => {
                this.error = err?.error?.message || 'Failed to import policy rules.';
                this.notifyService.showError(this.error!, 'Error');
              }
            });
        },
        error: (err) => {
          this.importing = false;
          this.error = err?.error?.message || 'Failed to load source policy rules.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  private buildImportRequest(rule: any, overwriteExisting: boolean): any {
    const payload = {
      ruleCode: String(rule.ruleCode || '').trim(),
      ruleType: String(rule.ruleType || 'Requirement').trim(),
      isBlocking: rule.isBlocking ?? true,
      orderIndex: Number(rule.orderIndex || 0),
      configJson: this.asNullableString(rule.configJson)
    };

    if (!payload.ruleCode) return null;

    const existing = this.items.find(x =>
      String(x.ruleCode || '').trim().toUpperCase() === payload.ruleCode.toUpperCase()
    );

    if (existing?.id) {
      return overwriteExisting
        ? this.adminService.updatePolicyRule(this.policyId, existing.id, payload)
        : null;
    }

    return this.adminService.createPolicyRule(this.policyId, payload);
  }

  save(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) return;

    this.saving = true;
    this.error = null;

    const payload = {
      ruleCode: String(this.form.value.ruleCode || '').trim(),
      ruleType: String(this.form.value.ruleType || '').trim(),
      isBlocking: !!this.form.value.isBlocking,
      orderIndex: Number(this.form.value.orderIndex || 0),
      configJson: this.buildConfigJson()
    };

    const request$ = this.editingId
      ? this.adminService.updatePolicyRule(this.policyId, this.editingId, payload)
      : this.adminService.createPolicyRule(this.policyId, payload);

    request$
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.notifyService.showSuccess(
            this.editingId ? 'Policy rule updated successfully.' : 'Policy rule created successfully.',
            'Success'
          );
          this.resetForm();
          this.loadItems();
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to save policy rule.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  edit(item: any): void {
    this.editingId = item.id;

    this.form.patchValue({
      ruleCode: item.ruleCode || '',
      ruleType: item.ruleType || 'Requirement',
      isBlocking: item.isBlocking ?? true,
      orderIndex: item.orderIndex ?? 0,
      configJson: item.configJson || ''
    });
    this.hydrateRuleBuilder(item);
  }

  openDeleteModal(item: any): void {
    if (!item?.id) return;
    this.rulePendingDelete = item;
  }

  confirmDelete(): void {
    if (!this.rulePendingDelete?.id) return;

    const item = this.rulePendingDelete;
    this.deletingId = item.id;
    this.error = null;

    this.adminService.deletePolicyRule(this.policyId, item.id)
      .pipe(finalize(() => (this.deletingId = null)))
      .subscribe({
        next: () => {
          this.notifyService.showSuccess('Policy rule deleted successfully.', 'Success');
          this.closeDeleteModal();
          this.rulePendingDelete = null;

          if (this.editingId === item.id) {
            this.resetForm();
          }

          this.loadItems();
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to delete policy rule.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  closeDeleteModal(): void {
    this.modalCleanup.hideById('deletePolicyRuleModal');
  }

  resetForm(): void {
    this.editingId = null;
    this.form.reset({
      ruleCode: '',
      ruleType: 'RequiredFields',
      isBlocking: true,
      orderIndex: 0,
      configJson: '',
      requiredFieldKeys: [],
      requiredDocumentTypeCodes: [],
      minimumAgeFieldKey: 'date_of_birth',
      minimumAge: 18,
      documentNotExpiredTypeCodes: [],
      selfieIdDocumentTypeCode: '',
      selfieDocumentTypeCode: 'SELFIE',
      sanctionsIdDocumentTypeCode: ''
    });
  }

  isRuleType(type: string): boolean {
    return String(this.form?.value?.ruleType || '') === type;
  }

  private buildConfigJson(): string | null {
    const ruleType = String(this.form.value.ruleType || '').trim();

    if (ruleType === 'RequiredFields') {
      return JSON.stringify({
        required: this.form.value.requiredFieldKeys || []
      });
    }

    if (ruleType === 'RequiredDocuments') {
      return JSON.stringify({
        required: this.form.value.requiredDocumentTypeCodes || []
      });
    }

    if (ruleType === 'MinimumAge') {
      return JSON.stringify({
        fieldKey: this.form.value.minimumAgeFieldKey || 'date_of_birth',
        minimumAge: Number(this.form.value.minimumAge || 18)
      });
    }

    if (ruleType === 'DocumentNotExpired') {
      return JSON.stringify({
        documentTypeCodes: this.form.value.documentNotExpiredTypeCodes || []
      });
    }

    if (ruleType === 'SelfieMatch') {
      return JSON.stringify({
        idDocumentTypeCode: this.form.value.selfieIdDocumentTypeCode || '',
        selfieDocumentTypeCode: this.form.value.selfieDocumentTypeCode || 'SELFIE'
      });
    }

    if (ruleType === 'SanctionsScreening') {
      return JSON.stringify({
        idDocumentTypeCode: this.form.value.sanctionsIdDocumentTypeCode || ''
      });
    }

    return this.asNullableString(this.form.value.configJson);
  }

  private hydrateRuleBuilder(item: any): void {
    const config = this.parseConfigJson(item.configJson);

    if (item.ruleType === 'RequiredFields') {
      this.form.patchValue({
        requiredFieldKeys: Array.isArray(config?.required) ? config.required : []
      });
    }

    if (item.ruleType === 'RequiredDocuments') {
      this.form.patchValue({
        requiredDocumentTypeCodes: Array.isArray(config?.required) ? config.required : []
      });
    }

    if (item.ruleType === 'MinimumAge') {
      this.form.patchValue({
        minimumAgeFieldKey: config?.fieldKey || 'date_of_birth',
        minimumAge: config?.minimumAge || 18
      });
    }

    if (item.ruleType === 'DocumentNotExpired') {
      this.form.patchValue({
        documentNotExpiredTypeCodes: Array.isArray(config?.documentTypeCodes)
          ? config.documentTypeCodes
          : []
      });
    }

    if (item.ruleType === 'SelfieMatch') {
      this.form.patchValue({
        selfieIdDocumentTypeCode: config?.idDocumentTypeCode || '',
        selfieDocumentTypeCode: config?.selfieDocumentTypeCode || 'SELFIE'
      });
    }

    if (item.ruleType === 'SanctionsScreening') {
      this.form.patchValue({
        sanctionsIdDocumentTypeCode: config?.idDocumentTypeCode || ''
      });
    }
  }

  private parseConfigJson(value: string | null | undefined): any {
    if (!value) return {};

    try {
      const parsed = JSON.parse(value);

      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      return {};
    }
  }

  back(): void {
    this.router.navigate(['/admin/compliance/policies', this.policyId, 'edit']);
  }

  asNullableString(value: any): string | null {
    const text = String(value || '').trim();
    return text ? text : null;
  }

  openFields(): void {
    if (!this.policyId) return;
    this.router.navigate(['/admin/compliance/policies', this.policyId, 'fields']);
  }

  openDocuments(): void {
    if (!this.policyId) return;
    this.router.navigate(['/admin/compliance/policies', this.policyId, 'documents']);
  }

  openRules(): void {
    if (!this.policyId) return;
    this.router.navigate(['/admin/compliance/policies', this.policyId, 'rules']);
  }

  get availableImportPolicies(): any[] {
    return this.policies.filter(x => String(x.id) !== String(this.policyId));
  }

  get sourcePolicyId() { return this.importForm.get('sourcePolicyId'); }
  get ruleCode() { return this.form.get('ruleCode'); }
}
